import { useCallback, useMemo } from "react";
import { useUserSettings } from "../contexts/useStorageContext";
import userSettingsService from "../service/userSettings.service";
import { ThemeMode } from "../types/userSettings";

export interface UseThemeModeResult {
  mode: ThemeMode;
  resolvedMode: "light" | "dark";
  setMode: (next: ThemeMode) => Promise<void>;
  toggle: () => Promise<void>;
}

const getSystemPrefersDark = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

export const useThemeMode = (): UseThemeModeResult => {
  const { userSettings, setUserSettings } = useUserSettings();
  const mode: ThemeMode = userSettings?.themeMode ?? "system";

  const resolvedMode = useMemo<"light" | "dark">(() => {
    if (mode === "dark") return "dark";
    if (mode === "light") return "light";
    return getSystemPrefersDark() ? "dark" : "light";
  }, [mode]);

  const setMode = useCallback(
    async (next: ThemeMode) => {
      if (!userSettings) return;
      const optimistic = { ...userSettings, themeMode: next };
      // Optimistic local update so the UI flips immediately.
      setUserSettings(optimistic);
      const updated = await userSettingsService.set(optimistic);
      // Only accept the server response if it actually looks like a settings
      // document (has _id/userId). Otherwise keep the optimistic value so the
      // UI does not flip back due to a malformed response.
      if (updated && updated._id && updated.userId) {
        setUserSettings(updated);
      }
    },
    [userSettings, setUserSettings]
  );

  const toggle = useCallback(async () => {
    await setMode(resolvedMode === "dark" ? "light" : "dark");
  }, [resolvedMode, setMode]);

  return { mode, resolvedMode, setMode, toggle };
};

export default useThemeMode;
